import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Link } from "@tanstack/react-router";
import { Download, ShieldCheck } from "lucide-react";
import { getAuctionCertificates } from "@/lib/certificates.functions";

/**
 * Buyer-facing badge showing that this listing carries a signed AI inspection
 * report. Revoked certificates are never advertised.
 */
export function AuctionCertificateBadge({ auctionId }: { auctionId: string }) {
  const fetchCerts = useServerFn(getAuctionCertificates);
  const { data } = useQuery({
    queryKey: ["auction-certificates", auctionId],
    queryFn: () => fetchCerts({ data: { auction_id: auctionId } }),
    staleTime: 5 * 60_000,
  });

  const cert = (data ?? []).find((c) => !c.revoked);
  if (!cert) return null;

  return (
    <div className="flex flex-wrap items-center gap-3 rounded-lg border border-emerald-500/30 bg-emerald-500/5 px-3 py-2">
      <ShieldCheck className="h-4 w-4 shrink-0 text-emerald-400" aria-hidden />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-emerald-200">AI Inspected</div>
        <p className="text-xs text-emerald-200/80">
          Signed inspection report <span className="font-mono">{cert.cert_code}</span> — advisory
          only, not a professional grade.
        </p>
      </div>
      <div className="flex items-center gap-2">
        <Link
          to="/verify/$code"
          params={{ code: cert.cert_code }}
          className="text-xs font-medium text-emerald-300 underline underline-offset-4"
        >
          Verify
        </Link>
        <a
          href={`/api/public/certificates/${cert.cert_code}/pdf`}
          className="inline-flex items-center gap-1 text-xs font-medium text-emerald-300 underline underline-offset-4"
        >
          <Download className="h-3.5 w-3.5" aria-hidden /> PDF
        </a>
      </div>
    </div>
  );
}
